import BigNumber from 'bignumber.js';
import { useIntl } from 'react-intl';
import { Range } from 'sonata-react-components';
import { normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';

import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { ContextualTargetingDistributionStyled } from './ContextualTargetingDistribution.styled';

import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';

type Props = {
  baseCurrency: BaseCurrencyInterface;
  totalBudget: number;
};

export function ContextualTargetingDistribution({ baseCurrency, totalBudget }: Props) {
  const { formatMessage } = useIntl();
  const {
    setProp,
    targeting: { contextualTargeting }
  } = useTargetingContext();

  const handleChange = (value: number) => {
    setProp('contextualTargeting', {
      ...contextualTargeting,
      distribution: {
        enabled: true,
        value: new BigNumber(value).dividedBy(totalBudget).toNumber()
      }
    });
  };

  const distributionValue = totalBudget * (contextualTargeting?.distribution?.value || 0.75);
  const normalizedBudget = normalizeWithUnit(totalBudget, baseCurrency);

  return (
    <ContextualTargetingDistributionStyled className="contextualTargetingDistribution">
      <Range
        id="contextual-targeting-distribution"
        max={totalBudget}
        onChange={value => handleChange(value as number)}
        type="slider"
        value={distributionValue}
      >
        <Range.Info>
          <Range.Info.Left className="contextualTargetingDistribution__rangeInfo">
            {formatMessage({
              defaultMessage: 'Applying Contextual Targeting',
              id: 'targeting.contentCategories.distribution.appliedBudget'
            })}
          </Range.Info.Left>
          <Range.Info.Right className="contextualTargetingDistribution__rangeInfo">
            {formatMessage({
              defaultMessage: 'No Contextual Targeting',
              id: 'targeting.contentCategories.distribution.notAppliedBudget'
            })}
          </Range.Info.Right>
        </Range.Info>
        <Range.SliderInput />
        <Range.Info className="contextualTargetingDistribution__rangeInfo">
          <Range.Info.Center className="contextualTargetingDistribution__rangeInfo">
            {formatMessage(
              {
                defaultMessage: `Total Budget: ${normalizedBudget}`,
                id: 'targeting.contentCategories.distribution.totalBudget'
              },
              {
                total: normalizedBudget
              }
            )}
          </Range.Info.Center>
        </Range.Info>
      </Range>
    </ContextualTargetingDistributionStyled>
  );
}
